import React from "react";

/**
 * App-wide React ErrorBoundary.
 *
 * Without this, any uncaught render error causes React 18 to unmount the
 * whole tree — customers see a completely blank page and have no way to
 * recover. This wraps the entire route tree so:
 *   1. The page never blanks. A friendly recovery card renders instead.
 *   2. The exact error + component stack is captured in the browser
 *      console AND surfaced back to the backend so we can diagnose the
 *      qty=100 (and any future) crash without waiting for a user to send
 *      us a stack trace.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    // In dev, log to console for immediate visibility. In production the
    // error is captured via the /api/client-errors beacon below, so the
    // console call is redundant and surfaces internal detail to end users.
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.error("[ArthursVault ErrorBoundary]", error, info?.componentStack);
    }
    // Best-effort report to backend for centralised diagnostics.
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL || ""}/api/client-errors`;
      const payload = JSON.stringify({
        message: String(error?.message || error),
        stack: String(error?.stack || "").slice(0, 4000),
        component_stack: String(info?.componentStack || "").slice(0, 4000),
        path: window.location.pathname + window.location.search,
        user_agent: navigator.userAgent,
      });
      if (navigator.sendBeacon) {
        navigator.sendBeacon(url, new Blob([payload], { type: "application/json" }));
      } else {
        fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body: payload, keepalive: true }).catch(() => {});
      }
    } catch (_e) { /* never break the UI */ }
  }

  handleReset = () => {
    this.setState({ error: null, info: null });
  };

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div data-testid="error-boundary" style={{ minHeight: "100vh", background: "#0b0c10", color: "#e9e4d7", display: "flex", alignItems: "center", justifyContent: "center", padding: "24px" }}>
        <div style={{ maxWidth: 520, textAlign: "center", border: "1px solid rgba(212,175,55,0.35)", borderRadius: 16, padding: 32, background: "rgba(11,12,16,0.65)" }}>
          <div style={{ fontFamily: "serif", fontSize: 28, fontWeight: 700, color: "#d4af37", marginBottom: 12 }}>
            Something went wrong
          </div>
          <p style={{ fontSize: 14, opacity: 0.8, marginBottom: 20 }}>
            The page hit an unexpected error. Your order and payment are safe — nothing has been charged. Please try again, and if it keeps happening send this to support with a screenshot.
          </p>
          <details style={{ textAlign: "left", fontSize: 11, background: "rgba(212,175,55,0.06)", padding: 12, borderRadius: 8, marginBottom: 20, opacity: 0.75, whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: 200, overflow: "auto" }}>
            <summary style={{ cursor: "pointer", marginBottom: 8 }}>Technical details</summary>
            <div>{String(this.state.error?.message || this.state.error)}</div>
            <div style={{ marginTop: 8, opacity: 0.6 }}>{this.state.info?.componentStack}</div>
          </details>
          <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
            <button data-testid="error-retry" onClick={this.handleReset} style={{ padding: "10px 22px", borderRadius: 999, background: "linear-gradient(180deg,#d4af37,#a17c1a)", color: "#0b0c10", border: 0, fontWeight: 600, cursor: "pointer" }}>
              Try again
            </button>
            <button data-testid="error-home" onClick={() => { window.location.href = "/"; }} style={{ padding: "10px 22px", borderRadius: 999, background: "transparent", color: "#e9e4d7", border: "1px solid rgba(212,175,55,0.5)", cursor: "pointer" }}>
              Back to home
            </button>
          </div>
        </div>
      </div>
    );
  }
}
